import { Component, OnInit, OnDestroy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  faSearch, faCog, faQuestionCircle, faTimes, faInfoCircle,
  faLightbulb, faTags, faChevronRight, faUsers, faPalette,
  faShield, faDatabase, faGlobe, faBell, faMoon, faSun, faUserCog, faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { SearchFilters } from '../../components/search/search.component';
import { ThemeService, Theme } from '../../services/theme.service';
import { TenantService } from '../../services/tenant.service';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  UsersService,
  UserPreferences,
  DEFAULT_USER_PREFERENCES,
  DEFAULT_MAX_SIMULATION_FILES,
  MAX_SIMULATION_FILES_LIMIT
} from '../../services/users.service';
import { normalizeForSearch } from '../../utils/text-search.util';

interface SettingOption {
  id: string;
  name: string;
  description: string;
  icon: any;
  route: string;
  enabled: boolean;
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    FontAwesomeModule,
    TranslateModule,
    HeaderActionsComponent,
    NgSelectModule
],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit, OnDestroy {
  settingOptions: SettingOption[] = [];
  filteredSettings: SettingOption[] = [];
  searchTerm: string = '';

  private langChangeSubscription?: Subscription;

  headerConfig: HeaderActionsConfig = {
    showAddButton: false,
    searchConfig: {
      showTextSearch: true,
      showTagFilter: false,
      textPlaceholder: 'SEARCH.PLACEHOLDER.SETTINGS',
      tagPlaceholder: 'SEARCH.PLACEHOLDER.TAGS'
    }
  };

  currentFilters: SearchFilters = {
    searchTerm: '',
    selectedTag: ''
  };

  faSearch = faSearch;
  faCog = faCog;
  faQuestionCircle = faQuestionCircle;
  faTimes = faTimes;
  faInfoCircle = faInfoCircle;
  faLightbulb = faLightbulb;
  faTags = faTags;
  faChevronRight = faChevronRight;
  faUsers = faUsers;
  faPalette = faPalette;
  faShield = faShield;
  faDatabase = faDatabase;
  faGlobe = faGlobe;
  faBell = faBell;
  faMoon = faMoon;
  faSun = faSun;
  faUserCog = faUserCog;
  faChartLine = faChartLine;

  currentTheme: Theme = 'light';
  maxSimulationFiles = DEFAULT_MAX_SIMULATION_FILES;
  readonly maxSimulationFilesLimit = MAX_SIMULATION_FILES_LIMIT;

  get isAdmin(): boolean {
    return sessionStorage.getItem('role') === 'ADMIN';
  }

  constructor(
    private router: Router,
    private translate: TranslateService,
    private themeService: ThemeService,
    private tenantService: TenantService,
    private usersService: UsersService
  ) {}

  ngOnInit(): void {
    this.currentTheme = this.themeService.currentTheme;

    this.translate.get('SETTINGS.TAGS.TITLE').subscribe(() => {
      this.loadSettingOptions();
    });

    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.loadSettingOptions();
    });

    if (this.isAdmin) {
      this.usersService.getMe().subscribe({
        next: (response) => {
          const prefs = response.user?.preferences;
          if (prefs?.max_simulation_files) {
            this.maxSimulationFiles = prefs.max_simulation_files;
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  loadSettingOptions(): void {
    const userRole = sessionStorage.getItem('role');
    const isAdmin = userRole === 'ADMIN';

    this.settingOptions = [
      {
        id: 'preferences',
        name: this.translate.instant('SETTINGS.PREFERENCES.TITLE'),
        description: this.translate.instant('SETTINGS.PREFERENCES.DESCRIPTION'),
        icon: this.faUserCog,
        route: '/settings/preferences',
        enabled: true
      },
      {
        id: 'tags',
        name: this.translate.instant('SETTINGS.TAGS.TITLE'),
        description: this.translate.instant('SETTINGS.TAGS.DESCRIPTION'),
        icon: this.faTags,
        route: '/settings/tags',
        enabled: true
      },
      {
        id: 'users',
        name: this.translate.instant('SETTINGS.USERS.TITLE'),
        description: this.translate.instant('SETTINGS.USERS.DESCRIPTION'),
        icon: this.faUsers,
        route: '/settings/users',
        enabled: isAdmin
      },
      {
        id: 'queue',
        name: this.translate.instant('SETTINGS.QUEUE.TITLE'),
        description: this.translate.instant('SETTINGS.QUEUE.DESCRIPTION'),
        icon: this.faTags,
        route: '/settings/queue',
        enabled: isAdmin
      },
      {
        id: 'delfos_server',
        name: this.translate.instant('SETTINGS.PREFERENCES.DELFOS_SERVER'),
        description: this.translate.instant('SETTINGS.DELFOS_SERVER.DESCRIPTION'),
        icon: this.faCog,
        route: '/settings/configurations/delfos_server',
        enabled: isAdmin
      },
      {
        id: 'delfos_llm',
        name: this.translate.instant('SETTINGS.PREFERENCES.DELFOS_LLM'),
        description: this.translate.instant('SETTINGS.DELFOS_LLM.DESCRIPTION'),
        icon: this.faCog,
        route: '/settings/configurations/delfos_llm',
        enabled: isAdmin
      },
      {
        id: 'di2winocr_server',
        name: this.translate.instant('SETTINGS.PREFERENCES.OCR_SERVER'),
        description: this.translate.instant('SETTINGS.DI2WINOCR_SERVER.DESCRIPTION'),
        icon: this.faCog,
        route: '/settings/configurations/di2winocr_server',
        enabled: isAdmin
      },
      {
        id: 'di2winocr_features',
        name: this.translate.instant('SETTINGS.PREFERENCES.OCR_FEATURES'),
        description: this.translate.instant('SETTINGS.DI2WINOCR_FEATURES.DESCRIPTION'),
        icon: this.faCog,
        route: '/settings/configurations/di2winocr_features',
        enabled: isAdmin
      },
      {
        id: 'retriever',
        name: this.translate.instant('SETTINGS.PREFERENCES.RETRIEVER'),
        description: this.translate.instant('SETTINGS.RETRIEVER.DESCRIPTION'),
        icon: this.faCog,
        route: '/settings/configurations/retriever',
        enabled: isAdmin
      },
      {
        id: 'evaluation',
        name: this.translate.instant('SETTINGS.BENCHMARK.TITLE'),
        description: this.translate.instant('SETTINGS.BENCHMARK.DESCRIPTION'),
        icon: this.faDatabase,
        route: '/settings/evaluation',
        enabled: isAdmin
      },
      {
        id: 'branding',
        name: this.translate.instant('SETTINGS.BRANDING.TITLE'),
        description: this.translate.instant('SETTINGS.BRANDING.DESCRIPTION'),
        icon: this.faPalette,
        route: '/settings/branding',
        enabled: isAdmin
      }
    ];

    this.applyFilters();
  }

  onFiltersChanged(filters: SearchFilters) {
    this.currentFilters = filters;
    this.searchTerm = filters.searchTerm;
    this.applyFilters();
  }

  onSearchCleared() {
    this.currentFilters.searchTerm = '';
    this.searchTerm = '';
    this.applyFilters();
  }

  onAllFiltersCleared() {
    this.currentFilters = {
      searchTerm: '',
      selectedTag: ''
    };
    this.searchTerm = '';
    this.filteredSettings = [...this.settingOptions];
  }

  private applyFilters(): void {
    if (!this.currentFilters.searchTerm.trim()) {
      this.filteredSettings = [...this.settingOptions];
      return;
    }

    const term = normalizeForSearch(this.currentFilters.searchTerm);
    this.filteredSettings = this.settingOptions.filter(setting =>
      normalizeForSearch(setting.name).includes(term) ||
      normalizeForSearch(setting.description).includes(term)
    );
  }

  onSearch(): void {
    this.currentFilters.searchTerm = this.searchTerm;
    this.applyFilters();
  }

  navigateToSetting(setting: SettingOption): void {
    if (setting.enabled) {
      this.router.navigate(this.tenantService.buildRoute(setting.route));
    }
  }

  currentLanguage = this.translate.currentLang || 'pt';

  languageOptions = [
    { value: 'pt', label: '🇧🇷 Português' },
    { value: 'en', label: '🇺🇸 English' },
    { value: 'es', label: '🇪🇸 Español' }
  ];

  themeOptions = [
    { value: 'light', label: 'THEME.LIGHT' },
    { value: 'dark', label: 'THEME.DARK' }
  ];

  maxSimulationFilesOptions = Array.from(
    { length: MAX_SIMULATION_FILES_LIMIT / DEFAULT_MAX_SIMULATION_FILES },
    (_, i) => (i + 1) * DEFAULT_MAX_SIMULATION_FILES
  );

  changeLanguage(lang: string) {
    this.currentLanguage = lang;
    this.translate.use(lang);
    sessionStorage.setItem('selectedLanguage', lang);
    this.persistPreferences({ ui_language: lang as 'pt' | 'en' | 'es' });
  }

  changeTheme(theme: Theme) {
    this.currentTheme = theme;
    this.themeService.setTheme(theme);
    this.persistPreferences({ theme });
  }

  changeMaxSimulationFiles(value: number) {
    const clamped = Math.min(Math.max(Math.round(value) || DEFAULT_MAX_SIMULATION_FILES, 1), MAX_SIMULATION_FILES_LIMIT);
    this.maxSimulationFiles = clamped;
    this.persistPreferences({ max_simulation_files: clamped });
  }

  private persistPreferences(partial: Partial<UserPreferences>): void {
    this.usersService.getMe().subscribe({
      next: (response) => {
        const current: UserPreferences = { ...DEFAULT_USER_PREFERENCES, ...(response.user?.preferences || {}) };
        const merged: UserPreferences = { ...current, ...partial };
        this.usersService.updatePreferences(merged).subscribe();
      }
    });
  }

  trackById(index: number, item: SettingOption): string {
    return item.id;
  }
}
